import React, { useEffect, useState } from "react";
import API from "../../api";
import LoadingSpinner from "../LoadingSpinner";
import Card from "../ui/Card";
import { CURRENT_SEASON } from "../../constants/season";

const TABLE_COMPETITIONS = [
  { id: "premier_league", label: "Premier League" },
  { id: "laliga", label: "LaLiga" },
  { id: "bundesliga", label: "Bundesliga" },
  { id: "serie_a", label: "Serie A" },
];

const fmt = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const pointsBadge = (points) => {
  if (points === null || points === undefined) {
    return "bg-gray-100 text-gray-600";
  }
  if (points >= 5) return "bg-green-100 text-green-800";
  if (points >= 3) return "bg-emerald-100 text-emerald-800";
  if (points >= 1) return "bg-amber-100 text-amber-800";
  return "bg-red-100 text-red-700";
};

export default function AdminAuditPanel() {
  const [section, setSection] = useState("logins");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [logins, setLogins] = useState([]);
  const [users, setUsers] = useState([]);
  const [matchPredictions, setMatchPredictions] = useState([]);
  const [tableOverview, setTableOverview] = useState([]);
  const [tableDetail, setTableDetail] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [competition, setCompetition] = useState("laliga");
  const [season, setSeason] = useState(CURRENT_SEASON);
  const [error, setError] = useState("");

  const loadSummary = async () => {
    const { data } = await API.get("/admin/audit/summary");
    setSummary(data);
  };

  const loadSection = async () => {
    setLoading(true);
    setError("");
    try {
      if (section === "logins") {
        const params = {};
        if (selectedUserId) params.userId = selectedUserId;
        const { data } = await API.get("/admin/audit/logins", { params });
        setLogins(data.logins || []);
      } else if (section === "users") {
        const { data } = await API.get("/admin/audit/users", {
          params: { search: userSearch || undefined, limit: 100 },
        });
        setUsers(data.users || []);
      } else if (section === "match") {
        const params = { season, limit: 150 };
        if (selectedUserId) params.userId = selectedUserId;
        if (competition) params.competition = competition;
        const { data } = await API.get("/admin/audit/match-predictions", { params });
        setMatchPredictions(data.predictions || []);
      } else if (section === "table") {
        if (selectedUserId) {
          const { data } = await API.get("/admin/audit/table-predictions", {
            params: { userId: selectedUserId, competition, season },
          });
          setTableDetail(data);
          setTableOverview([]);
        } else {
          const { data } = await API.get("/admin/audit/table-predictions", {
            params: { competition, season },
          });
          setTableOverview(data.overview || []);
          setTableDetail(null);
        }
      } else if (section === "sync") {
        const { data } = await API.get("/admin/audit/sync-logs", { params: { limit: 40 } });
        setSyncLogs(data.logs || []);
      }
    } catch (err) {
      console.error("Audit load failed:", err);
      setError(err.response?.data?.message || err.message || "Failed to load audit data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary().catch(() => {});
  }, []);

  useEffect(() => {
    loadSection();
  }, [section, selectedUserId, competition, season, userSearch]);

  const sections = [
    { key: "logins", label: "Logins" },
    { key: "users", label: "Users" },
    { key: "match", label: "Match predictions" },
    { key: "table", label: "Table predictions" },
    { key: "sync", label: "Sync logs" },
  ];

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Users", value: summary.totals?.users },
            { label: "Logins (24h)", value: summary.totals?.loginsLast24h },
            { label: "Predictions (7d)", value: summary.totals?.predictionsLast7d },
            { label: "Sync runs (7d)", value: summary.totals?.syncRunsLast7d },
          ].map((item) => (
            <div key={item.label} className="rounded-lg border bg-white p-4">
              <div className="text-2xl font-bold text-gray-900">{item.value ?? 0}</div>
              <div className="text-sm text-gray-500">{item.label}</div>
            </div>
          ))}
        </div>
      )}

      <Card title="Audit explorer">
        <div className="flex flex-wrap gap-2 mb-4">
          {sections.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSection(s.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                section === s.key
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          {(section === "logins" || section === "match" || section === "table") && (
            <input
              type="number"
              min="1"
              placeholder="Filter by user ID"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          )}
          {section === "users" && (
            <input
              type="search"
              placeholder="Search username or email"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="min-w-[220px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          )}
          {(section === "match" || section === "table") && (
            <>
              <select
                value={competition}
                onChange={(e) => setCompetition(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {TABLE_COMPETITIONS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
              <input
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </>
          )}
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {loading ? (
          <LoadingSpinner message="Loading audit data..." />
        ) : (
          <>
            {section === "logins" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">When</th>
                      <th className="p-2 text-left">User</th>
                      <th className="p-2 text-left">Method</th>
                      <th className="p-2 text-left">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logins.map((row) => (
                      <tr key={row.id} className="border-b">
                        <td className="p-2 whitespace-nowrap">{fmt(row.created_at)}</td>
                        <td className="p-2">
                          <div className="font-medium">{row.username}</div>
                          <div className="text-gray-500 text-xs">{row.email}</div>
                        </td>
                        <td className="p-2 capitalize">{row.method}</td>
                        <td className="p-2 font-mono text-xs">{row.ip_address || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {logins.length === 0 && (
                  <p className="text-gray-500 text-center py-6">No login events yet.</p>
                )}
              </div>
            )}

            {section === "users" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">User</th>
                      <th className="p-2 text-left">Last login</th>
                      <th className="p-2 text-left">Joined</th>
                      <th className="p-2 text-right">Match preds</th>
                      <th className="p-2 text-right">Table preds</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b">
                        <td className="p-2">
                          <div className="font-medium">
                            {u.username}{" "}
                            <span className="text-gray-400 font-normal">#{u.id}</span>
                          </div>
                          <div className="text-gray-500 text-xs">{u.email}</div>
                        </td>
                        <td className="p-2 whitespace-nowrap">{fmt(u.last_login_at)}</td>
                        <td className="p-2 whitespace-nowrap">{fmt(u.created_at)}</td>
                        <td className="p-2 text-right">{u.match_predictions}</td>
                        <td className="p-2 text-right">{u.table_predictions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {section === "match" && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">Match</th>
                      <th className="p-2 text-left">User</th>
                      <th className="p-2 text-left">Predicted</th>
                      <th className="p-2 text-left">Actual</th>
                      <th className="p-2 text-left">Points</th>
                      <th className="p-2 text-left">Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchPredictions.map((row) => (
                      <tr key={row.id} className="border-b align-top">
                        <td className="p-2">
                          <div className="font-medium">
                            {row.home_team} vs {row.away_team}
                          </div>
                          <div className="text-xs text-gray-500">
                            {row.competition} · {fmt(row.match_date)}
                          </div>
                        </td>
                        <td className="p-2">
                          <div>{row.username}</div>
                          <div className="text-xs text-gray-500">{row.email}</div>
                        </td>
                        <td className="p-2 font-mono">
                          {row.predicted_home_score}-{row.predicted_away_score}
                        </td>
                        <td className="p-2 font-mono">
                          {row.home_score != null ? `${row.home_score}-${row.away_score}` : "—"}
                        </td>
                        <td className="p-2">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${pointsBadge(
                              row.scoring?.points
                            )}`}
                          >
                            {row.scoring?.points ?? "—"}
                          </span>
                        </td>
                        <td className="p-2 text-gray-700">{row.scoring?.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {matchPredictions.length === 0 && (
                  <p className="text-gray-500 text-center py-6">No match predictions found.</p>
                )}
              </div>
            )}

            {section === "table" && !selectedUserId && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">User</th>
                      <th className="p-2 text-right">Score</th>
                      <th className="p-2 text-right">Teams</th>
                      <th className="p-2 text-left">Evaluated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableOverview.map((row) => (
                      <tr key={row.user_id} className="border-b">
                        <td className="p-2">
                          <button
                            type="button"
                            className="text-left hover:underline"
                            onClick={() => setSelectedUserId(String(row.user_id))}
                          >
                            <div className="font-medium">{row.username}</div>
                            <div className="text-xs text-gray-500">{row.email}</div>
                          </button>
                        </td>
                        <td className="p-2 text-right font-semibold">{row.score}</td>
                        <td className="p-2 text-right">{row.teams_predicted}</td>
                        <td className="p-2">{fmt(row.evaluated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {section === "table" && selectedUserId && tableDetail && (
              <div className="space-y-3">
                <div className="text-sm text-gray-600">
                  User #{tableDetail.userId} · {tableDetail.competition} · {tableDetail.season} ·{" "}
                  <strong>{tableDetail.totalPoints ?? 0} pts</strong>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-2 text-left">Team</th>
                        <th className="p-2 text-right">Predicted</th>
                        <th className="p-2 text-right">Actual</th>
                        <th className="p-2 text-right">Delta</th>
                        <th className="p-2 text-right">Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tableDetail.breakdown || []).map((row) => (
                        <tr key={`${row.team_name}-${row.predicted}`} className="border-b">
                          <td className="p-2">{row.team_name}</td>
                          <td className="p-2 text-right">{row.predicted}</td>
                          <td className="p-2 text-right">{row.actual ?? "—"}</td>
                          <td className="p-2 text-right">
                            {row.delta != null ? row.delta : "—"}
                          </td>
                          <td className="p-2 text-right font-semibold">{row.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {section === "sync" && (
              <div className="space-y-2">
                {syncLogs.map((log) => (
                  <div key={log.id} className="rounded-lg border bg-gray-50 p-3 text-sm">
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="font-semibold">
                        {log.type} · {log.competition || "all"}
                      </span>
                      <span className="text-gray-500">{fmt(log.created_at)}</span>
                    </div>
                    <pre className="mt-2 text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(log.summary, null, 2)}
                    </pre>
                  </div>
                ))}
                {syncLogs.length === 0 && (
                  <p className="text-gray-500 text-center py-6">No sync logs recorded.</p>
                )}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
